import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DpEmployer, listEmployersApi } from "../api/settingsApi";
import {
  DpIrrfDeductionMode,
  DpPayrollPolicy,
  getPayrollPolicyApi,
  IRRF_DEDUCTION_MODE_LABELS,
  savePayrollPolicyApi,
} from "../api/payrollApi";
import { formatDateOnly, toDateInputValue } from "../utils/format";

type Form = {
  effectiveFrom: string;
  advanceDay: string;
  paymentBusinessDay: string;
  dailyDivisor: string;
  irrfOnAdvance: boolean;
  irrfDeductionMode: DpIrrfDeductionMode;
  patRegistered: boolean;
  patMaxEmployeePct: string;
  dsrOnOvertime: boolean;
  overtimeWorkdayPct: string;
  overtimeRestDayPct: string;
  nightPct: string;
  fgtsPct: string;
  fgtsApprenticePct: string;
};

const toForm = (p: DpPayrollPolicy): Form => ({
  // Sem vigência salva, a primeira passa a valer desde o início de 2026.
  effectiveFrom: p.effectiveFrom ? toDateInputValue(p.effectiveFrom) : "2026-01-01",
  advanceDay: String(p.advanceDay),
  paymentBusinessDay: String(p.paymentBusinessDay),
  dailyDivisor: String(p.dailyDivisor),
  irrfOnAdvance: p.irrfOnAdvance,
  irrfDeductionMode: p.irrfDeductionMode,
  patRegistered: p.patRegistered,
  patMaxEmployeePct: String(Number(p.patMaxEmployeePct)),
  dsrOnOvertime: p.dsrOnOvertime,
  overtimeWorkdayPct: String(Number(p.overtimeWorkdayPct)),
  overtimeRestDayPct: String(Number(p.overtimeRestDayPct)),
  nightPct: String(Number(p.nightPct)),
  fgtsPct: String(Number(p.fgtsPct)),
  fgtsApprenticePct: String(Number(p.fgtsApprenticePct)),
});

const NUMBER_FIELDS: Array<{ key: keyof Form; label: string; hint?: string; step?: string }> = [
  { key: "advanceDay", label: "Dia do adiantamento", step: "1" },
  { key: "paymentBusinessDay", label: "Salário pago no Nº dia útil do mês seguinte", hint: "Sábado conta como dia útil.", step: "1" },
  { key: "dailyDivisor", label: "Divisor do dia (mensalista)", hint: "Mês comercial: 30.", step: "1" },
  { key: "overtimeWorkdayPct", label: "Hora extra em dia de trabalho (%)", hint: "Mínimo legal: 50%." },
  { key: "overtimeRestDayPct", label: "Hora extra em domingo e feriado (%)", hint: "Usual: 100%." },
  { key: "nightPct", label: "Adicional noturno (%)", hint: "Mínimo legal: 20%." },
  { key: "patMaxEmployeePct", label: "Desconto máximo no VR/VA pelo PAT (%)", hint: "Até 20% do benefício." },
  { key: "fgtsPct", label: "FGTS (%)" },
  { key: "fgtsApprenticePct", label: "FGTS do aprendiz (%)" },
];

/** Aba "Política de pagamento" em Cadastros: regras da Eco (não legais) usadas pela folha. */
export default function PayrollPolicySettings({ token }: { token: string }) {
  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [employerId, setEmployerId] = useState("");
  const [policy, setPolicy] = useState<DpPayrollPolicy | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listEmployersApi(token)
      .then((rows) => {
        const active = rows.filter((e) => e.isActive);
        setEmployers(active);
        if (active[0]) setEmployerId(active[0].id);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar empregadores."));
  }, [token]);

  const load = useCallback(async () => {
    if (!employerId) return;
    try {
      const current = await getPayrollPolicyApi(token, employerId);
      setPolicy(current);
      setForm(toForm(current));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar a política.");
    }
  }, [token, employerId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !employerId) return;
    if (!form.effectiveFrom) return toast.error("Informe a data a partir da qual a política vale.");
    const n = (key: keyof Form) => Number(form[key]);
    setSaving(true);
    try {
      await savePayrollPolicyApi(token, employerId, {
        effectiveFrom: form.effectiveFrom,
        advanceDay: n("advanceDay"),
        paymentBusinessDay: n("paymentBusinessDay"),
        dailyDivisor: n("dailyDivisor"),
        irrfOnAdvance: form.irrfOnAdvance,
        irrfDeductionMode: form.irrfDeductionMode,
        patRegistered: form.patRegistered,
        patMaxEmployeePct: n("patMaxEmployeePct"),
        dsrOnOvertime: form.dsrOnOvertime,
        overtimeWorkdayPct: n("overtimeWorkdayPct"),
        overtimeRestDayPct: n("overtimeRestDayPct"),
        nightPct: n("nightPct"),
        fgtsPct: n("fgtsPct"),
        fgtsApprenticePct: n("fgtsApprenticePct"),
      });
      toast.success("Política salva. Competências abertas usam a nova regra ao recalcular.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a política.");
    } finally {
      setSaving(false);
    }
  };

  if (employers.length === 0) {
    return <p className="dpNotice dpNotice--warning">Cadastre o empregador antes de definir a política.</p>;
  }
  if (!form || !policy) return <div className="dpEmpty">Carregando...</div>;

  return (
    <form onSubmit={save} className="dpSection">
      {employers.length > 1 ? (
        <div className="dpFormGroup" style={{ marginBottom: 16 }}>
          <label htmlFor="policyEmployer">Empregador</label>
          <select id="policyEmployer" className="dpSelect" value={employerId} onChange={(e) => setEmployerId(e.target.value)}>
            {employers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.tradeName || e.legalName}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <p className={`dpNotice ${policy.isDefault ? "dpNotice--warning" : ""}`}>
        {policy.isDefault
          ? "Usando o padrão do sistema (mínimos legais, adiantamento no dia 20, salário no 5º dia útil, PAT). Salve para registrar a política da empresa."
          : `Política vigente desde ${formatDateOnly(policy.effectiveFrom)}. Salvar com a mesma data corrige; com data nova, cria outra vigência.`}
      </p>

      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor="policyFrom">Vigente a partir de *</label>
          <input
            id="policyFrom"
            type="date"
            className="dpInput"
            value={form.effectiveFrom}
            onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
            disabled={saving}
          />
        </div>
        {NUMBER_FIELDS.map((field) => (
          <div key={field.key} className="dpFormGroup">
            <label htmlFor={`policy-${field.key}`}>{field.label}</label>
            <input
              id={`policy-${field.key}`}
              type="number"
              step={field.step ?? "0.01"}
              min="0"
              className="dpInput"
              value={form[field.key] as string}
              onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              disabled={saving}
            />
            {field.hint ? <span className="dpFormHint">{field.hint}</span> : null}
          </div>
        ))}
        <div className="dpFormGroup">
          <label htmlFor="policyIrrfMode">Dedução do IR</label>
          <select
            id="policyIrrfMode"
            className="dpSelect"
            value={form.irrfDeductionMode}
            onChange={(e) => setForm({ ...form, irrfDeductionMode: e.target.value as DpIrrfDeductionMode })}
            disabled={saving}
          >
            {(Object.keys(IRRF_DEDUCTION_MODE_LABELS) as DpIrrfDeductionMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {IRRF_DEDUCTION_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "16px 0" }}>
        {(
          [
            ["patRegistered", "Empresa inscrita no PAT (limita o desconto do VR/VA)"],
            ["dsrOnOvertime", "Pagar DSR sobre horas extras e adicional noturno"],
          ] as Array<[keyof Form, string]>
        ).map(([key, label]) => (
          <label key={key} className="dpCheckboxRow">
            <input
              type="checkbox"
              checked={form[key] as boolean}
              onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
              disabled={saving}
            />{" "}
            {label}
          </label>
        ))}
      </div>

      <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
        Salvar política
      </button>
    </form>
  );
}
